import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchDashboard } from "../../../api/dashboard";
import { apiFetch } from "../../../api/apiFetch";
import { BEHAVIOR_CHANGED_EVENT } from "../../behavior/utils/invalidation";
import {
  buildGoalSwitchOptions,
  buildProgressStory,
  buildVisionText,
  buildWeekPreview,
  buildWeekDays,
  buildWeeklyActionRows,
  buildWhyText,
  getDaysRemaining,
  mergeGoalData,
  parseGoalDueDate,
} from "../utils/planSelectors";

export default function usePlanData() {
  const [dashboard, setDashboard] = useState(null);
  const [visionData, setVisionData] = useState(null);
  const [planWeekState, setPlanWeekState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedGoalId, setSelectedGoalId] = useState(null);

  const loadPlan = useCallback(async ({ silent = false } = {}) => {
    if (!silent || !dashboard) {
      setLoading(true);
    }
    setError("");
    try {
      const dashboardData = await fetchDashboard();
      setDashboard(dashboardData);

      const visionId = dashboardData?.vision?.id;
      if (visionId) {
        try {
          const data = await apiFetch(`/dashboard/vision/${visionId}/data/?include_day_state=true`);
          setVisionData(data);
          setPlanWeekState(data?.plan_week_state || null);
        } catch (err) {
          console.error("Failed to load plan vision series", err);
          setVisionData(null);
          setPlanWeekState(null);
          setError(err?.message || "Some plan details are unavailable right now.");
        }
      } else {
        setVisionData(null);
        setPlanWeekState(null);
      }
    } catch (err) {
      console.error("Failed to load plan data", err);
      setError(err?.message || "Unable to load your plan right now.");
    } finally {
      if (!silent || !dashboard) {
        setLoading(false);
      }
    }
  }, [dashboard]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  useEffect(() => {
    function handleBehaviorChanged(event) {
      const targets = event?.detail?.targets;
      if (Array.isArray(targets) && !targets.includes("plan") && !targets.includes("dashboard")) {
        return;
      }
      loadPlan({ silent: true });
    }

    function handleGenericChanged() {
      loadPlan({ silent: true });
    }

    window.addEventListener(BEHAVIOR_CHANGED_EVENT, handleBehaviorChanged);
    window.addEventListener("trustbud:changed", handleGenericChanged);
    return () => {
      window.removeEventListener(BEHAVIOR_CHANGED_EVENT, handleBehaviorChanged);
      window.removeEventListener("trustbud:changed", handleGenericChanged);
    };
  }, [loadPlan]);

  const vision = dashboard?.vision || null;
  const goals = useMemo(() => vision?.goals || [], [vision]);
  const activeGoals = useMemo(
    () => goals.filter((goal) => !goal.completed_at),
    [goals]
  );

  useEffect(() => {
    if (!activeGoals.length) {
      setSelectedGoalId(null);
      return;
    }
    if (!selectedGoalId || !activeGoals.some((goal) => goal.id === selectedGoalId)) {
      setSelectedGoalId(activeGoals[0].id);
    }
  }, [activeGoals, selectedGoalId]);

  const detailedGoalMap = useMemo(() => {
    const entries = (visionData?.goals || []).map((goal) => [goal.id, goal]);
    return new Map(entries);
  }, [visionData]);

  const selectedSummaryGoal = useMemo(() => {
    if (!activeGoals.length) return null;
    return activeGoals.find((goal) => goal.id === selectedGoalId) || activeGoals[0];
  }, [activeGoals, selectedGoalId]);

  const selectedGoal = useMemo(() => {
    if (!selectedSummaryGoal) return null;
    return mergeGoalData(selectedSummaryGoal, detailedGoalMap.get(selectedSummaryGoal.id));
  }, [selectedSummaryGoal, detailedGoalMap]);

  const weekDays = useMemo(() => buildWeekDays(), []);
  const actionRows = useMemo(
    () => buildWeeklyActionRows(selectedGoal?.commitments || [], weekDays),
    [selectedGoal, weekDays]
  );
  const weekPreview = useMemo(
    () => buildWeekPreview(selectedGoal?.commitments || [], weekDays, undefined, undefined, planWeekState, selectedGoal?.id || null),
    [planWeekState, selectedGoal, weekDays]
  );
  const progressStory = useMemo(
    () => buildProgressStory(selectedGoal),
    [selectedGoal]
  );

  const dueDate = parseGoalDueDate(selectedGoal);
  const daysRemaining = getDaysRemaining(dueDate);

  return {
    loading,
    error,
    dashboard,
    vision,
    selectedGoalId,
    setSelectedGoalId,
    goalSwitchOptions: buildGoalSwitchOptions(activeGoals),
    selectedGoal,
    selectedGoalDueDate: dueDate,
    selectedGoalDaysRemaining: daysRemaining,
    whyText: buildWhyText(selectedGoal, vision),
    visionText: buildVisionText(vision),
    actionRows,
    weekPreview,
    progressStory,
    loadPlan,
  };
}
